import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    formatFindSlipNumber,
    sampleInputSchema,
    SAMPLE_TYPE_OPTIONS,
    SAMPLING_METHOD_OPTIONS,
    type FindDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    type SampleDocType
} from '@ast-data/shared';
import {
    joinedListSortComparator,
    dateColumn,
    type GridColDef
} from '../../lib/dataGrid';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { withRecentValues } from '../../components/recentValues';
import { usePersonUserId } from '../users/personUserId';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import {
    useDetailColumnHeader,
    useFindingRefFilterColumns
} from './findingRefLabels';
import { useFindingRefsColumn } from './useFindingRefsColumn';
import SampleFindsCell, { type SampleFindRef } from './SampleFindsCell';
import { compareNatural } from '../../utils/compare';

// Module-level so the field list's identity is stable across renders.
const SAMPLE_TYPE_EDITOR = {
    kind: 'suggest',
    options: SAMPLE_TYPE_OPTIONS
} as const;
const SAMPLING_METHOD_EDITOR = {
    kind: 'suggest',
    options: SAMPLING_METHOD_OPTIONS
} as const;

/**
 * The Probe fields editable in the grid: the two picked values and who took
 * it. The Entnahmegrund and the Beschreibung are prose.
 */
export function useInlineSampleFields(
    /** Every Probe in the project — for the values already used. */
    samples: readonly SampleDocType[]
): InlineField[] {
    const { resolve } = usePersonUserId();

    return useMemo(
        () =>
            withRecentValues(
                [
                    { field: 'sampleType', editor: SAMPLE_TYPE_EDITOR },
                    { field: 'samplingMethod', editor: SAMPLING_METHOD_EDITOR },
                    {
                        field: 'sampler',
                        editor: { kind: 'person' },
                        derive: (value) => ({
                            samplerId: resolve(String(value ?? ''))
                        })
                    }
                ],
                samples
            ),
        [resolve, samples]
    );
}

/**
 * The prose fields and the own Lage — in the column picker and the filter
 * panel, hidden by default.
 */
const SAMPLE_DETAIL_FIELDS = [
    'samplingReason',
    'description',
    'area',
    'trench',
    'planum',
    'profile'
] as const;

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_SAMPLE_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(SAMPLE_DETAIL_FIELDS.map((field) => [field, false])),
    ...HIDDEN_AUDIT_COLUMNS
};

export function useSamplesColumns({
    findings,
    planums,
    profiles,
    finds,
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    /** The Funde the rows may have become — to name them by Fundzettel. */
    finds: readonly FindDocType[];
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineSampleFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: SampleDocType) => void;
    onDelete: (row: SampleDocType) => void;
    onHistory: (row: SampleDocType) => void;
}): GridColDef<SampleDocType>[] {
    const intl = useIntl();
    const actionsColumn = useRecordActionsColumn<SampleDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<SampleDocType>();
    const inlineEdit = useInlineEditColumn<SampleDocType>(
        sampleInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<SampleDocType>(
        (row) => row.recordedAt
    );
    const header = (field: string) =>
        intl.formatMessage({ id: `sample.field.${field}` });
    const detailHeader = useDetailColumnHeader('sample');

    const refsColumn = useFindingRefsColumn<SampleDocType>({
        headerId: 'sample.field.descriptions',
        findings,
        planums,
        profiles
    });
    const findingFilterColumns = useFindingRefFilterColumns<SampleDocType>(
        findings,
        planums,
        profiles
    );

    const findById = useMemo(
        () => new Map(finds.map((find) => [find.id, find])),
        [finds]
    );
    // The Funde a row became, in Fundzettel order. A Fund since deleted simply
    // drops out.
    const findsOf = (row: SampleDocType): SampleFindRef[] =>
        row.findIds
            .map((id) => findById.get(id))
            .filter((find): find is FindDocType => Boolean(find))
            .map((find) => ({
                id: find.id,
                label: formatFindSlipNumber(find),
                to: `/office/projects/${find.projectId}/finds/${find.id}/edit`
            }))
            .sort((firstFind, secondFind) =>
                compareNatural(firstFind.label, secondFind.label)
            );

    const editable = (field: string): Partial<GridColDef<SampleDocType>> =>
        inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {};

    return [
        {
            ...dateColumn<SampleDocType>({
                field: 'recordedAt',
                headerName: header('recordedAt'),
                width: 130
            }),
            // Bold like every grid's key column — a Probe has no number.
            cellClassName: 'grid-cell-bold'
        },
        isoWeekColumn,
        {
            field: 'sampleType',
            headerName: header('sampleType'),
            width: 160,
            ...editable('sampleType')
        },
        { ...refsColumn, width: 240 },
        {
            field: 'samplingMethod',
            headerName: header('samplingMethod'),
            width: 160,
            ...editable('samplingMethod')
        },
        {
            field: 'sampler',
            headerName: header('sampler'),
            width: 150,
            ...editable('sampler')
        },
        {
            field: 'findIds',
            headerName: header('findIds'),
            width: 200,
            sortComparator: joinedListSortComparator,
            // The Fundzettelnummern as text, for the quick filter and the
            // export; the cell draws them as chips.
            valueGetter: (_value, row) =>
                findsOf(row)
                    .map((find) => find.label)
                    .join(', '),
            renderCell: ({ row }) => <SampleFindsCell finds={findsOf(row)} />
        },
        ...findingFilterColumns,
        ...SAMPLE_DETAIL_FIELDS.map<GridColDef<SampleDocType>>((field) => ({
            field,
            headerName: detailHeader(field),
            width: 200
        })),
        ...auditColumns,
        actionsColumn
    ];
}
